import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PageBar from '../../components/PageBar';
import PageLoading from '../../components/PageLoading';
import ListGrid from '../../components/ListGrid';
import Dialog from '../../components/Dialog';
import { useAuth } from '../../components/AuthContext';
import { useToast } from '../../components/ToastContext';
import {
  SYSTEMS, GROUPS, STATES, STATE_FILTERS, ROUTE_WORDS, PAGE,
  groupOf, loadPage, loadCounts, answer, confirmGroup,
} from './itemMappingData';

/*
  SETTINGS -> ITEM MAPPING, 5 October 2026.

  **Which ClaimSoft and Account Edge numbers belong to which product.** The
  catalog knows a product by the vendor's own part number; every line of a
  ClaimSoft transfer order is a ClaimSoft number. Until a number is mapped, a
  real transfer order cannot be read.

  **Every suggestion here was worked out from the owner's own chain**, and he
  said of it *"this can not be used as a 100% source of truth but it is a good
  starting point."* So the screen shows HOW each one was found beside it, and
  the answer is always his: yes, no, or not now.

  **He works it a group at a time, surest first**, and "Both routes agree" can
  be said yes to all at once, which is the one group he can approve in bulk.

  **What it does not do yet, said on the screen rather than hidden:** pick a
  product by hand for one that found nothing, and move a yes into the real item
  record. The second is a separate step shown to him before it runs.
*/

function product(p) {
  if (!p) return null;
  return {
    vendor: p.vendor ? p.vendor.name : 'no vendor',
    part: p.vendor_part_number,
    words: p.our_description || p.vendor_description || '',
  };
}

function Answer({ state }) {
  const tone = {
    waiting:   { ink: 'var(--text-secondary)', bg: 'var(--hover)' },
    confirmed: { ink: 'var(--success-ink)',    bg: 'var(--success-bg)' },
    rejected:  { ink: 'var(--critical-ink)',     bg: 'var(--critical-bg)' },
    set_aside: { ink: 'var(--warning-ink)',    bg: 'var(--warning-bg)' },
  }[state];
  return (
    <span title={STATES[state].says} style={{
      display: 'inline-block', padding: '3px 9px', borderRadius: 'var(--oh-radius-chip)',
      background: tone.bg, color: tone.ink, fontSize: 11.5, fontWeight: 700, whiteSpace: 'nowrap',
    }}>
      {STATES[state].label}
    </span>
  );
}

export default function ItemMapping() {
  const { user } = useAuth();
  const toast = useToast();

  const [system, setSystem] = useState('claimsoft');
  const [group, setGroup] = useState('agree');
  const [state, setState] = useState('waiting');
  const [search, setSearch] = useState('');
  const [typed, setTyped] = useState('');

  const [rows, setRows] = useState(null);
  const [more, setMore] = useState(false);
  const [counts, setCounts] = useState(null);
  const [failed, setFailed] = useState(null);
  const [busy, setBusy] = useState(null);
  const [bulk, setBulk] = useState(false);

  /* The search waits for him to stop typing, because each letter is a request
     to a list of 34,857. */
  useEffect(() => {
    const t = setTimeout(() => setSearch(typed), 300);
    return () => clearTimeout(t);
  }, [typed]);

  const filters = useMemo(() => ({ system, group, state, search }), [system, group, state, search]);

  const refreshCounts = useCallback(async () => {
    const r = await loadCounts({ system, state });
    if (!r.error) setCounts(r.counts);
  }, [system, state]);

  const load = useCallback(async () => {
    setRows(null);
    const r = await loadPage(filters, 0);
    /* A read that fails is not an empty group. Drawing one as the other is this
       project's oldest fault, and here it would read as "nothing left to do". */
    if (r.error) { setFailed(r.error.message); setRows([]); return; }
    setFailed(null);
    setRows(r.rows);
    setMore(r.rows.length === PAGE);
  }, [filters]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setCounts(null); refreshCounts(); }, [refreshCounts]);

  const onSystem = key => {
    setSystem(key);
    setGroup(GROUPS[key][0].key);
  };

  const loadMore = async () => {
    const r = await loadPage(filters, rows.length);
    if (r.error) { toast.error('Could not read more just now: ' + r.error.message); return; }
    setRows(prev => [...prev, ...r.rows]);
    setMore(r.rows.length === PAGE);
  };

  /* One answer changes one row on screen, and its rival if it has one. The
     list is not re-read, so he keeps his place. */
  const give = useCallback(async (row, next) => {
    setBusy(row.id);
    const res = await answer(row, next, user && user.id);
    setBusy(null);
    if (!res.ok) { toast.error(res.error ? res.error.message : 'That did not save. Nothing was changed.'); return; }
    const changed = new Map([[row.id, res.row.state], ...(res.others || []).map(o => [o.id, o.state])]);
    setRows(prev => prev.map(r => (changed.has(r.id) ? { ...r, state: changed.get(r.id) } : r)));
    if (res.otherError) toast.error('That saved, but the other suggestion for ' + row.number + ' could not be changed: ' + res.otherError.message);
    else if (res.others && res.others.length) {
      toast.success(next === 'confirmed'
        ? row.number + ': yes, and no to the other suggestion for it'
        : row.number + ': back to waiting, and the other suggestion for it too');
    } else {
      toast.success(row.number + ': ' + (next === 'waiting' ? 'back to waiting' : STATES[next].label.toLowerCase()));
    }
    refreshCounts();
  }, [user, toast, refreshCounts]);

  const g = groupOf(system, group);
  const canBulk = system === 'claimsoft' && group === 'agree' && state === 'waiting' && !search.trim();
  const bulkCount = counts ? counts.agree : 0;

  const runBulk = async () => {
    setBusy('bulk');
    const res = await confirmGroup({ system, group }, bulkCount, user && user.id);
    setBusy(null);
    setBulk(false);
    if (res.error) { toast.error('Nothing was confirmed: ' + res.error.message); return; }
    if (!res.ok) toast.error('Expected ' + bulkCount.toLocaleString() + ' and ' + res.changed.toLocaleString() + ' were confirmed. The list has been read again.');
    else toast.success(res.changed.toLocaleString() + ' confirmed');
    load();
    refreshCounts();
  };

  const isCs = system === 'claimsoft';
  const columns = [
    { key: 'number',  label: isCs ? 'ClaimSoft number' : 'Account Edge number', width: 'minmax(220px, 1.4fr)' },
    ...(isCs ? [{ key: 'via', label: 'Through Account Edge', width: 'minmax(130px, 0.8fr)' }] : []),
    { key: 'product', label: 'The product OnHand found', width: 'minmax(240px, 1.8fr)' },
    { key: 'how',     label: 'How it was found', width: 'minmax(160px, 1fr)' },
    { key: 'answer',  label: 'Your answer', width: 'minmax(250px, 1.2fr)' },
  ];

  const cells = r => {
    const p = product(r.product);
    const noRoute = r.route === 'not_found';
    return [
      <span>
        <span className="rpnum">{r.number}</span>
        {r.is_medicare && <span className="oh-pill-soon" style={{ marginLeft: 8 }} title="An MCR number: the same item as an ordinary number, made for Medicare billing">Medicare</span>}
        <span style={{ display: 'block', fontSize: 11.5, color: 'var(--text-secondary)', marginTop: 2 }}>
          {r.description || 'No description'}
        </span>
      </span>,
      ...(isCs ? [r.via_account_edge
        ? <span className="rpnum">{r.via_account_edge}</span>
        : <span style={{ color: 'var(--text-secondary)' }}>none</span>] : []),
      p ? (
        <span>
          <span style={{ fontWeight: 700 }}>{p.vendor}</span> <span className="rpnum">{p.part}</span>
          <span style={{ display: 'block', fontSize: 11.5, color: 'var(--text-secondary)', marginTop: 2 }}>{p.words}</span>
        </span>
      ) : (
        <span style={{ color: 'var(--text-secondary)' }}>
          {noRoute ? 'Nothing found' : 'Reaches Account Edge, but no product in the catalog'}
        </span>
      ),
      <span>
        {ROUTE_WORDS[r.route] || r.route}
        {r.warning === 'routes_disagree' && <span style={{ display: 'block', fontSize: 11, color: 'var(--warning-ink)', fontWeight: 700 }}>Disagrees with the other route</span>}
        {r.warning === 'shared' && <span style={{ display: 'block', fontSize: 11, color: 'var(--warning-ink)', fontWeight: 700 }}>Shared with other numbers</span>}
      </span>,
      <span style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        {r.state === 'waiting' ? (
          <>
            {!noRoute && (
              <>
                <button type="button" className="btn btn-outline" disabled={busy === r.id} onClick={() => give(r, 'confirmed')}>Yes</button>
                <button type="button" className="btn btn-outline" disabled={busy === r.id} onClick={() => give(r, 'rejected')}>No</button>
              </>
            )}
            <button type="button" className="btn btn-quiet" disabled={busy === r.id} onClick={() => give(r, 'set_aside')}>Not now</button>
          </>
        ) : (
          <>
            <Answer state={r.state} />
            <button type="button" className="btn btn-quiet" disabled={busy === r.id} onClick={() => give(r, 'waiting')}>Undo</button>
          </>
        )}
      </span>,
    ];
  };

  const total = counts ? counts.all : null;

  return (
    <>
      <PageBar
        title="Item Mapping"
        subtitle="Which ClaimSoft and Account Edge numbers belong to which product"
        subTabs={SYSTEMS}
        activeSubTab={system}
        onSubTabChange={onSystem}
        meta={total !== null && (
          <span><b>{total.toLocaleString()}</b> {STATE_FILTERS.find(s => s.key === state).label.toLowerCase()}</span>
        )}
        controls={
          <>
            <input className="tb-search" placeholder="Search a number"
              value={typed} onChange={e => setTyped(e.target.value)} />
            {/* **Capped to the room there is**, because a select is as wide as its
                longest choice, and "Through the Vendor Part Number column
                (1,862)" ran to the very edge of a phone with no margin. */}
            <select className="tb-select" value={group} onChange={e => setGroup(e.target.value)}
              style={{ maxWidth: '100%', minWidth: 0, textOverflow: 'ellipsis' }}
              aria-label="Which group of suggestions">
              {GROUPS[system].map(x => (
                <option key={x.key} value={x.key}>
                  {x.label}{counts ? ' (' + counts[x.key].toLocaleString() + ')' : ''}
                </option>
              ))}
            </select>
            <select className="tb-select" value={state} onChange={e => setState(e.target.value)}
              aria-label="Which answers to show">
              {STATE_FILTERS.map(x => <option key={x.key} value={x.key}>{x.label}</option>)}
            </select>
          </>
        }
      />

      <div className="card" style={{ padding: '14px 18px', marginBottom: 16, display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 320px', minWidth: 0 }}>
          <div style={{ fontWeight: 800 }}>{g.label}</div>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>{g.says}</div>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 6 }}>
            A yes here is a yes on this list. Moving it into the item record is the next step, and nothing has moved yet.
          </div>
        </div>
        {canBulk && bulkCount > 0 && (
          <button type="button" className="btn btn-primary" disabled={busy === 'bulk'} onClick={() => setBulk(true)}>
            Say yes to all {bulkCount.toLocaleString()}
          </button>
        )}
      </div>

      {failed ? (
        <div className="card" style={{ padding: 24 }}>
          <p>OnHand could not read the list just now.</p>
          <p style={{ color: 'var(--text-secondary)' }}>{failed}</p>
          <button type="button" className="btn" onClick={load}>Try again</button>
        </div>
      ) : rows === null ? (
        <PageLoading rows={8} />
      ) : (
        <>
          <ListGrid
            columns={columns}
            rows={rows}
            keyOf={r => r.id}
            cells={cells}
            layoutKey={'item-mapping-' + system}
            layoutTitle="The list's columns"
            empty={search.trim() ? 'No number in this group matches that.' : 'Nothing in this group with that answer.'}
          />
          {more && (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 16 }}>
              <button type="button" className="btn btn-outline" onClick={loadMore}>
                Show {PAGE} more
              </button>
            </div>
          )}
          <div style={{ textAlign: 'center', fontSize: 12, color: 'var(--text-secondary)', paddingBottom: 16 }}>
            Showing {rows.length.toLocaleString()}{counts && !search.trim() ? ' of ' + (counts[group] || 0).toLocaleString() : ''}
          </div>
        </>
      )}

      {bulk && (
        <Dialog
          kind="approve"
          title={'Say yes to all ' + bulkCount.toLocaleString() + '?'}
          says="Every ClaimSoft number still waiting in Both routes agree is confirmed as shown beside it: its Account Edge item, and its product where one was found. Each one can still be undone on its own."
          size="question"
          onClose={() => setBulk(false)}
          footer={<>
            <button type="button" className="btn btn-quiet" onClick={() => setBulk(false)}>Not yet</button>
            <button type="button" className="btn btn-primary" disabled={busy === 'bulk'} onClick={runBulk}>
              {busy === 'bulk' ? 'Confirming...' : 'Yes to all ' + bulkCount.toLocaleString()}
            </button>
          </>}
        />
      )}
    </>
  );
}
